import React, { useCallback, useContext, useEffect, useId, useRef, useState, SyntheticEvent } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useTranslation } from 'react-i18next';
import { Icon } from '@fikasio/react-ui-components';
import { TaskImportance } from 'services/tasks/types';
import { OpenStatusDropdownContext } from './TaskStatusDropdown';
import styles from './TasksList.module.css';

const IMPORTANCE_OPTIONS: Array<{
  color: string;
  icon: 'bars' | 'caret-down' | 'caret-up' | 'square';
  labelKey: 'importanceHigh' | 'importanceLow' | 'importanceMedium' | 'noImportance';
  value: TaskImportance | '';
}> = [
  { color: '#cecece', icon: 'square', labelKey: 'noImportance', value: '' },
  { color: '#2e7d32', icon: 'caret-down', labelKey: 'importanceLow', value: 'low' },
  { color: '#f5a623', icon: 'bars', labelKey: 'importanceMedium', value: 'medium' },
  { color: '#c62828', icon: 'caret-up', labelKey: 'importanceHigh', value: 'high' },
];

const importanceOption = (importance?: string | null) => (
  IMPORTANCE_OPTIONS.find(option => option.value === importance)
);

interface TaskImportanceToggleProps {
  'aria-expanded'?: boolean;
  alignToggle?: 'center' | 'start';
  importance?: string | null;
  onClick?: (event: SyntheticEvent) => void;
}

const TaskImportanceToggle = React.forwardRef<HTMLDivElement, TaskImportanceToggleProps>(({
  'aria-expanded': ariaExpanded,
  alignToggle = 'center',
  importance,
  onClick,
  ...props
}, ref) => {
  const { t } = useTranslation();
  const option = importanceOption(importance) ?? IMPORTANCE_OPTIONS[0];

  return (
    <div
      {...props}
      aria-expanded={ariaExpanded}
      aria-haspopup="menu"
      aria-label={t('importance')}
      className={`${styles.taskRowStatusToggle}${alignToggle === 'start' ? ` ${styles.taskStatusToggleStart}` : ''}`}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        onClick?.(event);
      }}
      ref={ref}
      role="button"
      style={{ color: option.color }}
      tabIndex={0}
      title={t(option.labelKey)}
    >
      <Icon
        name={option.icon}
        size="1x"
      />
    </div>
  );
});

TaskImportanceToggle.displayName = 'TaskImportanceToggle';

interface TaskImportanceDropdownProps {
  alignToggle?: 'center' | 'start';
  importance?: TaskImportance | null;
  onChange: (importance: TaskImportance | null) => void;
}

const TaskImportanceDropdown = ({
  alignToggle = 'center',
  importance,
  onChange,
}: TaskImportanceDropdownProps) => {
  const { t } = useTranslation();
  const dropdownId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const exclusiveDropdown = useContext(OpenStatusDropdownContext);
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = exclusiveDropdown
    ? exclusiveDropdown.openDropdownId === dropdownId
    : internalOpen;

  const setOpen = useCallback((nextShow: boolean) => {
    if (exclusiveDropdown) {
      exclusiveDropdown.setOpenDropdownId(nextShow ? dropdownId : null);
      return;
    }
    setInternalOpen(nextShow);
  }, [dropdownId, exclusiveDropdown]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && rootRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isOpen, setOpen]);

  const selectImportance = (next: TaskImportance | null) => {
    setOpen(false);
    onChange(next);
  };

  return (
    <Dropdown
      onToggle={nextShow => setOpen(nextShow)}
      ref={rootRef}
      show={isOpen}
    >
      <Dropdown.Toggle
        alignToggle={alignToggle}
        as={TaskImportanceToggle as React.ElementType}
        importance={importance}
      />
      <Dropdown.Menu
        data-testid="task-importance-menu"
        popperConfig={{
          modifiers: [
            { name: 'hide', enabled: false },
            { name: 'offset', options: { offset: [0, 6] } },
          ],
        }}
      >
        {
          IMPORTANCE_OPTIONS.map(option => (
            <Dropdown.Item
              active={(importance || '') === option.value}
              key={option.labelKey}
              onClick={() => selectImportance(option.value || null)}
            >
              <Icon
                name={option.icon}
                style={{
                  color: option.color,
                  marginRight: 10,
                  width: 18,
                }}
              />
              {t(option.labelKey)}
            </Dropdown.Item>
          ))
        }
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default TaskImportanceDropdown;
